
import { 
  doc, 
  updateDoc, 
  setDoc, 
  deleteDoc, 
  serverTimestamp, 
  collection, 
  writeBatch,
  arrayUnion,
  arrayRemove,
  FieldValue
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth'; // Import getAuth
import { db as firestoreInstance } from '@/utils/firebase';
import { 
  portfolioSchema, 
  Portfolio as PortfolioDataFromZod, 
  portfolioMemberSchema, // Restore this import for type inference
  portfolioMemberObjectSchema // Import the object schema
} from '@/schemas/portfolio'; // Zod inferred type
import { User as UserDataFromZod } from '@/schemas/user'; // Zod inferred type
import { MemberPermission, MemberRole, MemberStatus } from '@/types/schema'; 
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid'; // For generating member IDs if needed

// Define a type for the member data when adding/updating, before server timestamps
type PortfolioMemberPreValidation = Omit<z.infer<typeof portfolioMemberSchema>, 'id' | 'createdAt' | 'updatedAt' | 'invitedAt' | 'joinedAt'>;


export const usePortfolioOperations = (
  currentUser: UserDataFromZod | null | undefined, // UserDataFromZod is User from schemas/user which includes id
  // portfoliosFromFirebase is no longer needed as local state is removed
) => {
  // Local state for portfolios is removed as FirebaseProvider is the source of truth.
  
  const updatePortfolio = async (portfolioId: string, data: Partial<Omit<PortfolioDataFromZod, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      const validatedData = portfolioSchema.partial().parse(data);
      const portfolioRef = doc(firestoreInstance, 'portfolios', portfolioId);
      await updateDoc(portfolioRef, {
        ...validatedData,
        updatedAt: serverTimestamp(),
      });
      console.log(`usePortfolioOperations: Successfully updated portfolio ${portfolioId}`);
    } catch (error) {
      console.error(`usePortfolioOperations: Error updating portfolio ${portfolioId} in Firestore:`, error);
      if (error instanceof z.ZodError) {
        console.error("Validation errors:", error.errors);
      }
      throw error;
    }
  };
  
  const createPortfolio = async (portfolioData: Omit<PortfolioDataFromZod, 'id' | 'createdAt' | 'updatedAt' | 'members'>, ownerId: string): Promise<string> => {
    const newPortfolioId = doc(collection(firestoreInstance, 'portfolios')).id;
    
    // Create the initial member (owner) data for validation (without server timestamps)
    const ownerMemberDataForValidation: PortfolioMemberPreValidation = {
      userId: ownerId,
      email: currentUser?.email || null,
      firstName: currentUser?.firstName || null,
      lastName: currentUser?.lastName || null,
      role: 'Primary Owner',
      status: 'active',
      permissions: [], 
      avatarUrl: currentUser?.avatarUrl || null,
      avatarInitials: currentUser?.avatarInitials || null,
      // invitedAt and joinedAt will be serverTimestamp()
    };
     // Validate the owner member data (omitting fields that will be server timestamps)
    const validatedOwnerMemberInput = portfolioMemberObjectSchema.omit({ invitedAt: true, joinedAt: true }).parse(ownerMemberDataForValidation);
    
    // Assuming portfolioData (function input) is already validated for its own fields (e.g., by a form using Zod resolver)
    // We are primarily concerned with constructing the full object correctly here.

    // Construct the final document with server timestamps
    const finalDocumentData = {
      ...portfolioData, // Spread the incoming core portfolio data
      id: newPortfolioId,
      members: [{ 
        ...validatedOwnerMemberInput, // Spread the validated owner member core data
        invitedAt: new Date(), 
        joinedAt: new Date() 
      }],
      businessRefs: [], // Initialize businessRefs as an empty array
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    // We will parse the *entire* final document structure with portfolioSchema
    // but only *after* replacing serverTimestamp placeholders if direct parsing is an issue.
    // For now, let's try to parse it directly. If serverTimestamp causes issues with portfolioSchema,
    // we'll need to adjust portfolioSchema or the validation strategy.
    // The portfolioSchema's timestamp fields are unions that include the { _seconds, _nanoseconds } structure,
    // which serverTimestamp() does not immediately satisfy for Zod.
    // So, we validate parts and then assemble. The `finalDocumentData` is what goes to Firestore.
    // Zod validation should happen on inputs *before* they are mixed with FieldValues like serverTimestamp().

    // The `portfolioData` (function input) is typed as Omit<PortfolioDataFromZod, 'id' | 'createdAt' | 'updatedAt' | 'members'>.
    // We assume it has been validated by the caller (e.g., a form using Zod resolver) before being passed to this function.
    // The main validation concern within this function is for data constructed here, like the ownerMember.
    // The `validatedOwnerMemberInput` is already parsed.
    // The `finalDocumentData` is constructed for Firestore. It's not typically re-parsed by Zod if it contains FieldValues.
    
    try {
      const batch = writeBatch(firestoreInstance);
      const portfolioRef = doc(firestoreInstance, 'portfolios', newPortfolioId);
      batch.set(portfolioRef, finalDocumentData); // Pass the assembled data to Firestore

      // Add portfolioId to user's portfolios array using the ownerId parameter directly
      const userRef = doc(firestoreInstance, 'users', ownerId);
      batch.update(userRef, {
        portfolios: arrayUnion(newPortfolioId),
        updatedAt: serverTimestamp(), // Also update user's updatedAt
      });
      console.log(`usePortfolioOperations: Adding portfolio ${newPortfolioId} to user ${ownerId}'s portfolio list.`);
      
      await batch.commit();
      console.log(`usePortfolioOperations: Successfully created portfolio ${newPortfolioId}`);
      return newPortfolioId;
    } catch (error) {
      console.error(`usePortfolioOperations: Error creating portfolio in Firestore:`, error);
      if (error instanceof z.ZodError) {
        console.error("Validation errors:", error.errors);
      }
      throw error;
    }
  };
  
  const deletePortfolio = async (portfolioId: string, ownerId?: string) => {
    try {
      const batch = writeBatch(firestoreInstance);
      const portfolioRef = doc(firestoreInstance, 'portfolios', portfolioId);
      batch.delete(portfolioRef);

      // Remove portfolioId from user's portfolios array
      // First try to use the provided ownerId if available
      if (ownerId) {
        const userRef = doc(firestoreInstance, 'users', ownerId);
        batch.update(userRef, {
          portfolios: arrayRemove(portfolioId),
          updatedAt: serverTimestamp(), // Also update user's updatedAt
        });
        console.log(`usePortfolioOperations: Removing portfolio ${portfolioId} from user ${ownerId}'s portfolio list.`);
      } 
      // Fall back to currentUser if ownerId is not provided
      else if (currentUser && currentUser.id) {
        const userRef = doc(firestoreInstance, 'users', currentUser.id);
        batch.update(userRef, {
          portfolios: arrayRemove(portfolioId),
          updatedAt: serverTimestamp(), // Also update user's updatedAt
        });
        console.log(`usePortfolioOperations: Removing portfolio ${portfolioId} from user ${currentUser.id}'s portfolio list.`);
      } else {
        console.warn(`usePortfolioOperations: Could not determine user ID to remove portfolio ${portfolioId} from user's portfolio list.`);
      }

      // TODO: Consider implications for businesses linked in businessRefs.
      // This might involve more complex cleanup logic (e.g., unlinking or deleting businesses).
      // For now, only handling portfolio and user document.

      await batch.commit();
      console.log(`usePortfolioOperations: Successfully deleted portfolio ${portfolioId} and updated user ${currentUser?.id}`);
    } catch (error) {
      console.error(`usePortfolioOperations: Error deleting portfolio ${portfolioId} from Firestore:`, error);
      throw error;
    }
  };
  
  // --- Member Operations ---
  // These require careful handling of the 'members' array within a portfolio document.

  const addUserToPortfolio = async (
    portfolioId: string, 
    memberDataInput: PortfolioMemberPreValidation & { status?: MemberStatus } // Use the pre-validation type
  ) => {
    const portfolioRef = doc(firestoreInstance, 'portfolios', portfolioId);
    try {
      // Validate the input member data (omitting fields that will be server timestamps)
      const validatedMemberInput = portfolioMemberObjectSchema
        .omit({ invitedAt: true, joinedAt: true }) // Omit timestamp fields for this validation pass
        .parse(memberDataInput);

      // Construct the final member object for Firestore with server timestamps
      const memberToAddForFirestore = {
        ...validatedMemberInput, // Spread the validated data
        userId: memberDataInput.userId, // Ensure userId is from the original input
        email: memberDataInput.email || null,
        firstName: memberDataInput.firstName || null,
        lastName: memberDataInput.lastName || null,
        phone: memberDataInput.phone || null,
        avatarUrl: memberDataInput.avatarUrl || null,
        avatarInitials: memberDataInput.avatarInitials || null,
        permissions: memberDataInput.permissions || [],
        role: memberDataInput.role, // role is required by PortfolioMemberPreValidation (via portfolioMemberSchema)
        status: memberDataInput.status || 'invited', // Default to 'invited'
        invitedAt: serverTimestamp(),
        joinedAt: memberDataInput.status === 'active' ? serverTimestamp() : null,
      };
      // This object is not re-validated with portfolioMemberSchema if it contains FieldValue

      await updateDoc(portfolioRef, {
        members: arrayUnion(memberToAddForFirestore), // Use the Firestore-ready object
        updatedAt: serverTimestamp(),
      });
      console.log(`usePortfolioOperations: Successfully added user ${memberDataInput.userId} to portfolio ${portfolioId}`);

      // TODO: If user is new (invited), potentially create an invitation document or send an email.
      // TODO: Update the target user's document if they need a reference back to this portfolio (e.g., in a 'sharedPortfolios' array).
      // This depends on the data model for user-portfolio relationships beyond the owner.
    } catch (error) {
      console.error(`usePortfolioOperations: Error adding user to portfolio ${portfolioId}:`, error);
      if (error instanceof z.ZodError) {
        console.error("Validation errors:", error.errors);
      }
      throw error;
    }
  };
  
  const removeUserFromPortfolio = async (portfolioId: string, userIdToRemove: string) => {
    const portfolioRef = doc(firestoreInstance, 'portfolios', portfolioId);
    try {
      // To remove an item from an array, Firestore needs the exact object to remove.
      // This means we might need to fetch the portfolio, find the member, and then use arrayRemove.
      // This is a simplified version assuming we can construct enough of the object or have it.
      // A more robust way is to fetch, filter, and update the whole array.
      // For now, this is a placeholder for a more complex operation if direct object match fails.
      
      // Fetch the portfolio document to get the current members array
      // const portfolioSnap = await getDoc(portfolioRef);
      // if (!portfolioSnap.exists()) {
      //   throw new Error(`Portfolio ${portfolioId} not found.`);
      // }
      // const portfolioData = portfolioSnap.data() as PortfolioDataFromZod;
      // const memberToRemove = portfolioData.members?.find(member => member.userId === userIdToRemove);

      // if (!memberToRemove) {
      //   console.warn(`usePortfolioOperations: User ${userIdToRemove} not found in portfolio ${portfolioId}. No action taken.`);
      //   return;
      // }
      // This approach (get, then update) is not ideal for arrayRemove if we don't have the exact object.
      // Firestore's arrayRemove works by value. If the object in the array has server timestamps
      // that have resolved to actual dates, the object we construct here with serverTimestamp()
      // will NOT match.
      // A common pattern is to read the doc, filter the array in code, then overwrite the array.
      // This is more operations but more reliable for removing specific items by ID.

      // For simplicity in this step, we'll assume a more direct (but potentially less robust) removal
      // or that the member object is passed in. The TODOs in the original file imply this complexity.
      // Let's assume for now we are removing based on a simpler structure or will refine this.
      // A better approach for removing a member by userId:
      // 1. Read the portfolio document.
      // 2. Filter out the member with the given userId from the members array.
      // 3. Update the portfolio document with the new members array.
      // This requires getDoc and then updateDoc.

      // Placeholder for the more robust implementation:
      console.warn(`usePortfolioOperations.removeUserFromPortfolio: Robust implementation requires fetching and filtering members array for portfolio ${portfolioId}.`);
      // Example of a direct (but potentially problematic) arrayRemove if we had the exact object:
      // await updateDoc(portfolioRef, {
      //   members: arrayRemove(memberObjectToRemove), // memberObjectToRemove would need to be precise
      //   updatedAt: serverTimestamp(),
      // });

      // TODO: Update the removed user's document if they had a reference to this portfolio.
      console.log(`usePortfolioOperations: Placeholder for removing user ${userIdToRemove} from portfolio ${portfolioId}`);
    } catch (error) {
      console.error(`usePortfolioOperations: Error removing user from portfolio ${portfolioId}:`, error);
      throw error;
    }
  };

  const updatePortfolioMember = async (
    portfolioId: string,
    userIdToUpdate: string,
    memberUpdateData: Partial<PortfolioMemberPreValidation & { status?: MemberStatus }> // Use pre-validation type
  ) => {
    // This is complex because array element updates are not direct in Firestore.
    // Typically involves reading the document, modifying the array in code, and writing the entire array back.
    console.warn(`usePortfolioOperations.updatePortfolioMember: Robust implementation requires fetching, modifying, and overwriting members array for portfolio ${portfolioId}.`);
    // Placeholder logic
    // 1. getDoc(portfolioRef)
    // 2. Find member by userIdToUpdate in portfolioData.members
    // 3. Apply updates to that member object
    // 4. Validate the updated member object with portfolioMemberSchema
    // 5. updateDoc(portfolioRef, { members: updatedMembersArray, updatedAt: serverTimestamp() })
    console.log(`usePortfolioOperations: Placeholder for updating member ${userIdToUpdate} in portfolio ${portfolioId}`);
  };
  
  return {
    // portfolios, // Removed as local state is gone
    // setPortfolios, // Removed
    updatePortfolio,
    createPortfolio,
    deletePortfolio,
    addUserToPortfolio,
    removeUserFromPortfolio,
    updatePortfolioMember, // Added new function
  };
};
